import React, { useEffect, useState } from 'react';

import { Button } from '../../ui/Button/Button';
import { Modal } from '../../ui/Modal/Modal';
import styles from './AddSizeModal.module.scss';

export interface NewSizeValues {
    name: string;
    length: number | null;
    width: number | null;
    height: number | null;
    maximum_load_weight: number | null;
    transport_length?: number;
    transport_width?: number;
    transport_height?: number;
    transport_weight?: number;

    source_titles?: string[]
}

interface AddSizeModalProps {
    open: boolean;
    busy?: boolean;
    error?: string | null;
    onSubmit: (values: NewSizeValues) => void;
    onClose: () => void;
}

const SIZE_LIMIT = 10_000;
const WEIGHT_LIMIT = 5_000_000;

const FIELD_GROUPS = [
    {
        title: 'Габариты основного товара',
        required: true,
        fields: [
            { key: 'length', label: 'Длина, мм', limit: SIZE_LIMIT },
            { key: 'width', label: 'Ширина, мм', limit: SIZE_LIMIT },
            { key: 'height', label: 'Высота, мм', limit: SIZE_LIMIT },
            { key: 'weight', label: 'Вес, г', limit: WEIGHT_LIMIT },
        ],
    },
    {
        title: 'Транспортные габариты',
        required: false,
        fields: [
            { key: 'transport_length', label: 'Длина, мм', limit: SIZE_LIMIT },
            { key: 'transport_width', label: 'Ширина, мм', limit: SIZE_LIMIT },
            { key: 'transport_height', label: 'Высота, мм', limit: SIZE_LIMIT },
            { key: 'transport_weight', label: 'Вес, г', limit: WEIGHT_LIMIT },
        ],
    },
] as const;

const [PRODUCT_GROUP, TRANSPORT_GROUP] = FIELD_GROUPS;

type NumField = (typeof FIELD_GROUPS)[number]['fields'][number]['key'];
type NumFieldMeta = { readonly key: NumField; readonly limit: number };

const EMPTY: Record<NumField, string> = {
    length: '',
    width: '',
    height: '',
    weight: '',
    transport_length: '',
    transport_width: '',
    transport_height: '',
    transport_weight: '',
};

export function AddSizeModal({ open, busy, error, onSubmit, onClose }: AddSizeModalProps) {
    const [name, setName] = useState('');
    const [nums, setNums] = useState<Record<NumField, string>>(EMPTY);

    useEffect(() => {
        if (!open) return;

        setName('');
        setNums(EMPTY);
    }, [open]);

    if (!open) return null;

    const num = (key: NumField) => Number(nums[key]) || 0;

    const filledCount = (fields: readonly NumFieldMeta[]) =>
        fields.filter((field) => num(field.key) > 0).length;

    const isGroupOk = (fields: readonly NumFieldMeta[], required: boolean) => {
        const filled = filledCount(fields);

        if (filled === 0) return !required;
        if (filled !== fields.length) return false;

        return fields.every((field) => num(field.key) <= field.limit);
    };

    const hasTransport = filledCount(TRANSPORT_GROUP.fields) > 0;
    const limitsOk = FIELD_GROUPS.every((group) => isGroupOk(group.fields, group.required));
    const canSubmit = name.trim().length > 1 && limitsOk;

    const submit = () =>
        onSubmit({
            name: name.trim(),
            length: num('length') || null,
            width: num('width') || null,
            height: num('height') || null,
            maximum_load_weight: num('weight') || null,
            ...(hasTransport && {
                transport_length: num('transport_length'),
                transport_width: num('transport_width'),
                transport_height: num('transport_height'),
                transport_weight: num('transport_weight'),
            }),
        });

    return (
        <Modal
            open
            onClose={onClose}
            width={560}
            tone="accent"
            title="Добавить новый товар"
            footer={
                <>
                    <Button onClick={onClose}>Отмена</Button>
                    <Button variant="primary" disabled={!canSubmit || busy} onClick={submit}>
                        Добавить
                    </Button>
                </>
            }
        >
            <p className={styles.hint}>
                Запись попадёт в справочник как подтверждённая — в новых проектах габариты
                возьмутся отсюда, без повторного поиска.
            </p>

            <label className={styles.field}>
                <span className={styles.label1}>Наименование</span>
                <input
                    className={styles.input}
                    autoFocus
                    value={name}
                    placeholder="Например: Ноутбук ASUS Vivobook 15 X1504VA"
                    onChange={(event) => setName(event.target.value)}
                />
            </label>

            {error && <p className={styles.error}>{error}</p>}

            {FIELD_GROUPS.map((group) => (
                <section className={styles.group} key={group.title}>
                    <h4 className={styles.groupTitle}>
                        {group.title}
                        {!group.required && (
                            <span className={styles.optional}>необязательно</span>
                        )}
                    </h4>

                    <div className={styles.grid}>
                        {group.fields.map((field) => (
                            <label className={styles.field} key={field.key}>
                                <span className={styles.label}>{field.label}</span>
                                <input
                                    className={styles.input}
                                    inputMode="numeric"
                                    value={nums[field.key]}
                                    onChange={(event) =>
                                        setNums((prev) => ({
                                            ...prev,
                                            [field.key]: event.target.value.replace(/[^\d]/g, ''),
                                        }))
                                    }
                                />
                            </label>
                        ))}
                    </div>

                    {group === PRODUCT_GROUP && <div className={styles.divider} />}
                </section>
            ))}
        </Modal>
    );
}